import React, { useEffect } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { DisclosureBanner } from "../components/DisclosureBanner";
import { CTAButton } from "../components/CTAButton";
import { RelatedReports } from "../components/RelatedReports";
import { OptInBlueprint } from "../components/OptInBlueprint";
import { IMAGES } from "../lib/images";
import { useSEO, SITE } from "../lib/useSEO";
import {
    Feather,
    Waves,
    Moon,
    Sparkles,
    ArrowRight,
} from "lucide-react";

const FeatureCard = ({ icon: Icon, title, body, testId }) => (
    <div
        data-testid={testId}
        className="bg-white border border-[color:var(--brr-border)] rounded-2xl p-7 md:p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,40,51,0.25)]"
    >
        <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-6"
            style={{ background: "var(--brr-blue-soft)" }}
        >
            <Icon
                size={20}
                strokeWidth={1.5}
                style={{ color: "var(--brr-blue-hover)" }}
            />
        </div>
        <h3 className="text-xl sm:text-2xl mb-3">{title}</h3>
        <p className="text-[15px] leading-relaxed text-[color:var(--brr-ink-2)] font-light">
            {body}
        </p>
    </div>
);

const useReveal = () => {
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("in");
                        obs.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 },
        );
        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);
};

const HOME_TITLE =
    "The Better Rest Report | Premium Contour Pillow Editorial";
const HOME_DESCRIPTION =
    "An editorial review of the Premium Contour Pillow — silhouette, soft-blue breathable fabric, and the quiet design choices that shape better sleep comfort.";

const Home = () => {
    useReveal();

    const homeSchemas = [
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: SITE.name,
            url: SITE.origin + "/",
            inLanguage: "en-US",
            description: HOME_DESCRIPTION,
            publisher: {
                "@type": "Organization",
                name: SITE.name,
                url: SITE.origin,
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: HOME_TITLE,
            url: SITE.origin + "/",
            description: HOME_DESCRIPTION,
            inLanguage: "en-US",
            isPartOf: {
                "@type": "WebSite",
                name: SITE.name,
                url: SITE.origin,
            },
        },
    ];

    useSEO({
        title: HOME_TITLE,
        description: HOME_DESCRIPTION,
        path: "/",
        ogImage: IMAGES.pillowHero.src,
        schemas: homeSchemas,
    });

    return (
        <div className="App">
            <DisclosureBanner variant="top" />
            <Header />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative overflow-hidden"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-6 reveal">
                            <div className="flex items-center gap-4 mb-8">
                                <span className="brr-overline">
                                    Issue No. 07 · The Rest Edition
                                </span>
                                <span className="brr-rule" />
                            </div>
                            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
                                The quiet ritual
                                <br />
                                of a{" "}
                                <span className="italic text-[color:var(--brr-blue)]">
                                    better
                                </span>{" "}
                                night.
                            </h1>
                            <p className="mt-8 max-w-xl text-lg sm:text-xl font-light leading-relaxed text-[color:var(--brr-ink-2)]">
                                An editorial look at the{" "}
                                <em className="font-serif-display not-italic">
                                    Premium Contour Pillow
                                </em>{" "}
                                — a quietly considered object designed around
                                breathable fabric, an ergonomic profile, and a
                                soft-blue cover that feels like calm itself.
                            </p>

                            <div className="mt-10 flex flex-col sm:flex-row gap-5 sm:items-center">
                                <CTAButton
                                    label="Check Today's Offer"
                                    testId="hero-cta-check-offer"
                                />
                                <CTAButton
                                    variant="ghost"
                                    label="Read The Better Rest Report"
                                    testId="hero-cta-read-report"
                                />
                            </div>

                            <div className="mt-12 flex items-center gap-4 text-xs tracking-[0.18em] uppercase text-[color:var(--brr-ink-2)] opacity-80">
                                <span
                                    className="brr-rule"
                                    style={{ width: "32px" }}
                                />
                                <span>Editorial Review · 8-Minute Read</span>
                            </div>
                        </div>

                        <div className="lg:col-span-6 reveal">
                            <div className="relative">
                                <div
                                    className="hidden md:block absolute -inset-6 md:-inset-10 rounded-[40px]"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, var(--brr-blue-soft) 0%, var(--brr-bg-accent) 100%)",
                                    }}
                                />
                                <img
                                    data-testid="hero-image"
                                    src={IMAGES.pillowHero.src}
                                    alt={IMAGES.pillowHero.alt}
                                    loading="eager"
                                    fetchpriority="high"
                                    decoding="async"
                                    className="relative w-full aspect-[4/5] sm:aspect-auto sm:max-h-[78vh] object-cover sm:object-contain rounded-3xl bg-[color:var(--brr-blue-soft)]"
                                />
                                <div className="absolute -bottom-4 left-4 md:left-8 bg-white rounded-full px-5 py-3 border border-[color:var(--brr-border)] shadow-[0_18px_40px_-24px_rgba(28,40,51,0.25)]">
                                    <p className="text-[11px] tracking-[0.22em] uppercase font-semibold">
                                        New This Season
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* THE STORY BLOCK */}
            <section
                id="report"
                data-testid="story-section"
                style={{ background: "var(--brr-bg-2)" }}
                className="border-y border-[color:var(--brr-border)]"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
                        <aside className="lg:col-span-4">
                            <div className="lg:sticky lg:top-28 reveal">
                                <span className="brr-overline">
                                    The Report
                                </span>
                                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
                                    Read the
                                    <br />
                                    <span className="italic">
                                        Better Rest
                                    </span>{" "}
                                    Report.
                                </h2>
                                <p className="mt-6 text-[color:var(--brr-ink-2)] font-light leading-relaxed">
                                    A short editorial on why the right pillow
                                    quietly changes everything about a
                                    morning.
                                </p>
                                <div className="mt-8">
                                    <CTAButton
                                        label="Check Today's Offer"
                                        testId="story-cta-check-offer"
                                    />
                                </div>
                            </div>
                        </aside>

                        <div className="lg:col-span-8 reveal">
                            <p className="drop-cap text-[17px] sm:text-[18px] leading-[1.85] text-[color:var(--brr-ink)] font-light">
                                There is a kind of morning that begins the
                                night before — long before the alarm,
                                somewhere between the moment your head meets
                                the pillow and the soft, almost imperceptible
                                release of the day. The{" "}
                                <em className="font-serif-display">
                                    Premium Contour Pillow
                                </em>{" "}
                                is shaped around that small, quiet moment.
                                Not a miracle device, not a clinical
                                instrument — simply a thoughtfully designed
                                object built around three ideas: breathable
                                fabric, an ergonomic contour profile, and
                                materials chosen for the long, undramatic work
                                of comfort.
                            </p>
                            <p className="mt-7 text-[17px] sm:text-[18px] leading-[1.85] font-light text-[color:var(--brr-ink-2)]">
                                The cover is woven in a soft, airy blue — a
                                fabric that breathes the way good linen does,
                                quietly and consistently. Beneath it, a
                                contour silhouette traces the natural arc of
                                the head and shoulders. The point isn't to
                                fix anything; it's to remove a thousand small
                                frictions that we tolerate without noticing.
                            </p>

                            <figure className="my-12">
                                <img
                                    data-testid="story-image"
                                    src={IMAGES.pillowLifestyle.src}
                                    alt={IMAGES.pillowLifestyle.alt}
                                    loading="eager"
                                    className="w-full h-[44vh] md:h-[56vh] object-cover rounded-2xl"
                                />
                                <figcaption className="mt-3 text-xs tracking-[0.18em] uppercase opacity-60">
                                    Morning light, considered
                                </figcaption>
                            </figure>

                            <p className="text-[17px] sm:text-[18px] leading-[1.85] font-light text-[color:var(--brr-ink-2)]">
                                The most generous compliment we can pay an
                                object like this is that you stop thinking
                                about it. It disappears into the architecture
                                of a good night. And that, we think, is
                                exactly the point.
                            </p>

                            <div className="mt-10">
                                <a
                                    href="#features"
                                    data-testid="story-continue-link"
                                    className="btn-ghost"
                                >
                                    Continue to features
                                    <ArrowRight size={16} strokeWidth={1.5} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENTO FEATURE HIGHLIGHTS */}
            <section
                id="features"
                data-testid="features-section"
                className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-5 reveal">
                        <span className="brr-overline">Considered Design</span>
                        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
                            Three small things,
                            <br />
                            done <span className="italic">unusually well</span>.
                        </h2>
                        <p className="mt-6 text-[color:var(--brr-ink-2)] font-light leading-relaxed max-w-md">
                            We looked closely at the materials, the
                            silhouette, and the touch — and chose what felt
                            quietly right.
                        </p>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 reveal">
                        <FeatureCard
                            icon={Feather}
                            title="Breathable Fabric"
                            body="A soft-blue cover engineered to feel airy against the skin — quietly cool to the touch, undemanding through the night."
                            testId="feature-breathable"
                        />
                        <FeatureCard
                            icon={Waves}
                            title="Ergonomic Contour"
                            body="A profile shaped around the natural curve of the head and shoulders, designed to remove the small frictions of rest."
                            testId="feature-contour"
                        />
                        <FeatureCard
                            icon={Sparkles}
                            title="Premium Materials"
                            body="Considered fillings and finishes that hold their shape and breathe well — built for the long, ordinary work of every night."
                            testId="feature-materials"
                        />
                        <FeatureCard
                            icon={Moon}
                            title="Quiet Aesthetic"
                            body="An understated, neutral palette designed to disappear into any bedroom — softly modern, never loud."
                            testId="feature-aesthetic"
                        />
                    </div>
                </div>

                {/* Product gallery */}
                <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 reveal">
                    <div className="md:col-span-7 rounded-3xl overflow-hidden bg-[color:var(--brr-bg-accent)] aspect-[16/11]">
                        <img
                            data-testid="gallery-product"
                            src={IMAGES.pillowProduct.src}
                            alt={IMAGES.pillowProduct.alt}
                            loading="eager"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="md:col-span-5 grid grid-rows-2 gap-6 md:gap-8">
                        <div className="rounded-3xl overflow-hidden bg-[color:var(--brr-blue-soft)]">
                            <img
                                data-testid="gallery-side"
                                src={IMAGES.pillowSide.src}
                                alt={IMAGES.pillowSide.alt}
                                loading="eager"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="rounded-3xl overflow-hidden bg-[color:var(--brr-bg-accent)]">
                            <img
                                data-testid="gallery-detail"
                                src={IMAGES.pillowDetail.src}
                                alt={IMAGES.pillowDetail.alt}
                                loading="eager"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <CTAButton
                        label="Check Today's Offer"
                        testId="features-cta-check-offer"
                    />
                    <span className="text-xs tracking-[0.18em] uppercase opacity-60">
                        Availability subject to retailer · Editorial review only
                    </span>
                </div>
            </section>

            {/* TESTIMONIAL EDITORIAL */}
            <section
                data-testid="testimonial-section"
                style={{ background: "var(--brr-bg-accent)" }}
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 text-center reveal">
                    <span className="brr-overline">A Reader Wrote</span>
                    <blockquote className="mt-8 font-serif-display italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-[color:var(--brr-ink)]">
                        “I never knew a pillow could make a morning feel this
                        deliberate. It’s the smallest thing in the room — and
                        somehow, the one I notice most.”
                    </blockquote>
                    <div className="mt-8 flex items-center justify-center gap-4 text-xs tracking-[0.22em] uppercase opacity-70">
                        <span>Madeleine R.</span>
                        <span className="brr-rule" />
                        <span>Brooklyn, NY</span>
                    </div>
                </div>
            </section>

            {/* EDITOR'S NOTE / READER GUIDE */}
            <OptInBlueprint />

            <section
                data-testid="editor-note-section"
                className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 reveal">
                        <span className="brr-overline">Editor's Note</span>
                        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
                            What we look for
                            <br />
                            in a <span className="italic">good pillow</span>.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8 reveal">
                        {[
                            {
                                n: "01",
                                t: "Touch",
                                d: "A fabric that feels right the moment you put your hand on it. Quiet, airy, undemanding.",
                            },
                            {
                                n: "02",
                                t: "Shape",
                                d: "A contour that supports without performing. The best shape is the one you forget about.",
                            },
                            {
                                n: "03",
                                t: "Materials",
                                d: "Considered fillings that breathe and hold their form across long, ordinary weeks.",
                            },
                            {
                                n: "04",
                                t: "Aesthetic",
                                d: "An object that belongs in a bedroom — soft palette, modern silhouette, never loud.",
                            },
                        ].map((item) => (
                            <div
                                key={item.n}
                                data-testid={`editor-criterion-${item.n}`}
                                className="flex flex-col"
                            >
                                <span className="issue-tag text-2xl mb-3">
                                    {item.n}
                                </span>
                                <h3 className="text-xl mb-2">{item.t}</h3>
                                <p className="text-[15px] leading-relaxed text-[color:var(--brr-ink-2)] font-light">
                                    {item.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section
                data-testid="final-cta-section"
                style={{ background: "#1C2833" }}
                className="relative overflow-hidden"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div
                            className="lg:col-span-7 reveal"
                            style={{ color: "var(--brr-bg)" }}
                        >
                            <span
                                className="brr-overline"
                                style={{ color: "#A9C2D6" }}
                            >
                                Closing Note
                            </span>
                            <h2
                                className="font-serif-display text-3xl sm:text-4xl lg:text-6xl leading-[1.04] mt-6"
                                style={{ color: "var(--brr-bg)" }}
                            >
                                A small object that
                                <br />
                                <span className="italic text-[#A9C2D6]">
                                    changes the morning.
                                </span>
                            </h2>
                            <p className="mt-7 text-lg font-light leading-relaxed max-w-xl opacity-85">
                                If a great night begins the night before, it
                                begins here. See the latest offer on the
                                Premium Contour Pillow.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-5">
                                <CTAButton
                                    label="Check Today's Offer"
                                    testId="final-cta-check-offer"
                                    className="!bg-[color:var(--brr-bg)] !text-[color:var(--brr-ink)]"
                                />
                                <CTAButton
                                    variant="ghost"
                                    label="Read The Better Rest Report"
                                    testId="final-cta-read-report"
                                    style={{
                                        color: "var(--brr-bg)",
                                        borderBottomColor: "var(--brr-bg)",
                                    }}
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-5 reveal">
                            <img
                                data-testid="final-cta-image"
                                src={IMAGES.pillowFinalCta.src}
                                alt={IMAGES.pillowFinalCta.alt}
                                loading="eager"
                                className="w-full h-auto max-h-[60vh] object-contain rounded-3xl bg-[color:var(--brr-bg-accent)]"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <RelatedReports exclude="" limit={10} />
            <Footer />
        </div>
    );
};

export default Home;
